import type {
  Attribute,
  AttributeDataType,
  BulkProductsRequest,
  PatchAdminV1OrgProductsIdBody,
  PostAdminV1OrgProductsBody,
  PostAdminV1OrgProductsBodyAttributeValuesItem,
  PostAdminV1OrgProductsBodyOptionsItem,
  Product,
} from '@nucleus-commerce/api-client/admin';
import { fromZonedInput, type Money } from '@nucleus-commerce/core';

import { fromSeoOverride, toSeoOverride } from '@/features/seo';
import type { MetadataEntryForm } from '@/shared/forms/metadata-entry-schema';

import type { BulkScheduleForm } from '../schemas/bulk-schedule-schema';
import {
  DIMENSION_UNITS,
  type AttributeFormValue,
  type DimensionUnit,
  type OptionAxisForm,
  type OptionSwatchForm,
  type ProductFormInput,
  type ProductFormOutput,
} from '../schemas/product-form-schema';

import { fromWeight, toWeight } from './weight-mappers';

/**
 * Form ↔ API mappers. The form keeps UI-friendly shapes (empty strings, one uniform slot per
 * attribute); the API gets exactly its DTO: optional keys omitted on create, nulls on update so
 * a cleared field is cleared, attribute values and options replaced wholesale.
 */

export type AttributeShape = Pick<Attribute, 'id' | 'dataType'> & Partial<Pick<Attribute, 'unit'>>;
/** What the options mapper needs to tell an override apart from the dictionary value. */
export type OptionAttributeShape = Pick<Attribute, 'id' | 'label' | 'values'>;

/** The backend requires a handle on create; a blank field means "derive it from the title". */
export function slugify(value: string): string {
  return value
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

/** Stored option value for a number axis: `^[a-z0-9][a-z0-9_.-]*$`. "2.0 mm" → "2.0_mm". */
export function optionValueCode(label: string): string {
  return label
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9_.-]+/g, '_')
    .replace(/^[^a-z0-9]+/, '')
    .replace(/_+/g, '_');
}

const isDimensionUnit = (value: unknown): value is DimensionUnit =>
  typeof value === 'string' && (DIMENSION_UNITS as readonly string[]).includes(value);

/** The unit a dimension slot starts with: the attribute's own unit when it is one we offer. */
export function defaultDimensionUnit(attributeUnit: string | null | undefined): DimensionUnit {
  return isDimensionUnit(attributeUnit) ? attributeUnit : 'mm';
}

export const emptyAttributeValue = (unit: DimensionUnit = 'mm'): AttributeFormValue => ({
  ids: [],
  text: '',
  html: '',
  number: '',
  unit,
  bool: false,
});

const days = (v: string): number | null => (v === '' ? null : Number(v));

function attributeValueItem(
  attributeId: string,
  dataType: AttributeDataType,
  slot: AttributeFormValue,
): PostAdminV1OrgProductsBodyAttributeValuesItem | null {
  switch (dataType) {
    case 'select':
    case 'multiselect':
      return slot.ids.length > 0 ? { attributeId, attributeValueIds: slot.ids } : null;
    case 'text':
      return slot.text.trim() ? { attributeId, valueText: slot.text.trim() } : null;
    case 'rich_text':
      return slot.html.trim() ? { attributeId, valueJson: { html: slot.html } } : null;
    case 'number':
      return slot.number !== '' && Number.isFinite(Number(slot.number))
        ? { attributeId, valueNumber: Number(slot.number) }
        : null;
    case 'dimension':
      return slot.number !== '' && Number.isFinite(Number(slot.number))
        ? { attributeId, valueJson: { value: Number(slot.number), unit: slot.unit || 'mm' } }
        : null;
    case 'boolean':
      return { attributeId, valueBoolean: slot.bool };
  }
}

/** Only attributes the caller knows (the dictionary) are sent; stale slots are dropped. */
export function toAttributeValuesPayload(
  slots: Record<string, AttributeFormValue>,
  attributes: readonly AttributeShape[],
): PostAdminV1OrgProductsBodyAttributeValuesItem[] {
  const items: PostAdminV1OrgProductsBodyAttributeValuesItem[] = [];
  for (const attribute of attributes) {
    const slot = slots[attribute.id];
    if (!slot) continue;
    const item = attributeValueItem(attribute.id, attribute.dataType, slot);
    if (item) items.push(item);
  }
  return items;
}

/**
 * The attribute ids in the order `toAttributeValuesPayload` sends them, so a 422 on
 * `body.attributeValues.N` can be put on the attribute's own input.
 */
export function attributeValuesPayloadOrder(
  slots: Record<string, AttributeFormValue>,
  attributes: readonly AttributeShape[],
): string[] {
  return toAttributeValuesPayload(slots, attributes).map((item) => item.attributeId);
}

const sameSwatch = (
  a: OptionSwatchForm | null | undefined,
  b: OptionSwatchForm | null | undefined,
): boolean =>
  (a?.color ?? null) === (b?.color ?? null) && (a?.mediaId ?? null) === (b?.mediaId ?? null);

/**
 * Axes and values as the API wants them. The display name of an axis and the label or swatch
 * of a dictionary value travel only when they differ from the dictionary, so a dictionary
 * rename still flows through to products that never overrode it.
 */
export function toOptionsPayload(
  options: readonly OptionAxisForm[],
  attributes: readonly OptionAttributeShape[] = [],
): PostAdminV1OrgProductsBodyOptionsItem[] {
  const byId = new Map(attributes.map((a) => [a.id, a]));
  return options.map((axis) => {
    const attribute = byId.get(axis.attributeId);
    const name = axis.name.trim();
    // A saved option or value keeps what is stored when a key is left out, so going back to the
    // dictionary has to be said out loud.
    const backToAttribute = axis.optionId !== undefined && name === '' && attribute !== undefined;
    return {
      attributeId: axis.attributeId,
      ...(name && name !== attribute?.label ? { name } : {}),
      ...(backToAttribute ? { name: attribute.label } : {}),
      values: axis.values.map((v) => {
        if (v.attributeValueId) {
          const dictionary = attribute?.values.find((d) => d.id === v.attributeValueId);
          const label = v.label?.trim();
          const savedValue = v.valueId !== undefined && dictionary !== undefined;
          return {
            attributeValueId: v.attributeValueId,
            ...(label && label !== dictionary?.label ? { label } : {}),
            ...(savedValue && !label ? { label: dictionary.label } : {}),
            ...(v.swatch !== undefined && !sameSwatch(v.swatch, dictionary?.swatch)
              ? { swatch: v.swatch }
              : {}),
            ...(savedValue && v.swatch === undefined ? { swatch: dictionary.swatch } : {}),
          };
        }
        const typed = v.label?.trim();
        const label = typed !== undefined && typed !== '' ? typed : (v.value ?? '');
        return {
          value: v.value ?? optionValueCode(label),
          label,
          ...(v.swatch ? { swatch: v.swatch } : {}),
        };
      }),
    };
  });
}

export function toMetadata(entries: readonly MetadataEntryForm[]): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const entry of entries) {
    const key = entry.key.trim();
    if (!key) continue;
    out[key] = entry.json ? (JSON.parse(entry.value) as unknown) : entry.value;
  }
  return out;
}

export function fromMetadata(metadata: Record<string, unknown> | undefined): MetadataEntryForm[] {
  return Object.entries(metadata ?? {}).map(([key, value]) =>
    typeof value === 'string'
      ? { key, value, json: false }
      : { key, value: JSON.stringify(value), json: true },
  );
}

function shared(values: ProductFormOutput, attributes: readonly Attribute[]) {
  return {
    title: values.title,
    handle: values.handle || slugify(values.title),
    status: values.status,
    tags: values.tags,
    searchKeywords: values.searchKeywords,
    fulfillmentMode: values.fulfillmentMode,
    leadTimeDaysMin: days(values.leadTimeDaysMin),
    leadTimeDaysMax: days(values.leadTimeDaysMax),
    requiresDesignApproval: values.requiresDesignApproval,
    productTypeId: values.productTypeId || null,
    customizationIds: values.customizationIds,
    faq: values.faq,
    metadata: toMetadata(values.metadata),
    attributeValues: toAttributeValuesPayload(values.attributeValues, attributes),
    options: toOptionsPayload(values.options, attributes),
  };
}

const country = (v: string): string | null => (v === '' ? null : v.toUpperCase());

/**
 * Without `customizationIds` the backend starts the product on its type's defaults, so the key
 * travels only when someone picked some. `basePrice` makes the first save create the variants.
 */
export function toProductInput(
  values: ProductFormOutput,
  attributes: readonly Attribute[] = [],
): PostAdminV1OrgProductsBody {
  const seo = toSeoOverride(values.seo);
  const weight = toWeight(values.weight);
  const countryOfOrigin = country(values.countryOfOrigin);
  const { customizationIds, ...common } = shared(values, attributes);
  return {
    ...common,
    ...(customizationIds.length > 0 ? { customizationIds } : {}),
    ...(values.basePrice ? { variantSync: { basePrice: values.basePrice } } : {}),
    ...(values.subtitle ? { subtitle: values.subtitle } : {}),
    ...(values.vendor ? { vendor: values.vendor } : {}),
    ...(values.skuRoot ? { skuRoot: values.skuRoot } : {}),
    ...(values.descriptionHtml ? { descriptionHtml: values.descriptionHtml } : {}),
    ...(values.summary ? { summary: values.summary } : {}),
    ...(seo ? { seo } : {}),
    ...(weight ? { weight } : {}),
    ...(values.hsCode ? { hsCode: values.hsCode } : {}),
    ...(countryOfOrigin ? { countryOfOrigin } : {}),
  };
}

/**
 * Options are in the body only when `optionSync` says they changed, because the backend plans a
 * variant sync whenever they are there, and `variantSync` goes with them: the variants follow the
 * options instead of the save being refused. `basePrice` is null on a product with no variant at
 * all, where there is no price to create combinations at; the options then save on their own.
 */
export function toProductUpdate(
  values: ProductFormOutput,
  attributes: readonly Attribute[] = [],
  optionSync: { basePrice: Money | null } | null = null,
): PatchAdminV1OrgProductsIdBody {
  const { options, ...common } = shared(values, attributes);
  return {
    ...common,
    ...(optionSync ? { options } : {}),
    ...(optionSync?.basePrice ? { variantSync: { basePrice: optionSync.basePrice } } : {}),
    subtitle: values.subtitle || null,
    vendor: values.vendor || null,
    skuRoot: values.skuRoot || null,
    descriptionHtml: values.descriptionHtml || null,
    summary: values.summary || null,
    seo: toSeoOverride(values.seo),
    weight: toWeight(values.weight),
    hsCode: values.hsCode || null,
    countryOfOrigin: country(values.countryOfOrigin),
  };
}

export function emptyProductForm(): ProductFormInput {
  return {
    title: '',
    handle: '',
    subtitle: '',
    vendor: '',
    skuRoot: '',
    descriptionHtml: '',
    summary: '',
    faq: [],
    seo: fromSeoOverride(undefined),
    tags: [],
    searchKeywords: [],
    status: 'draft',
    fulfillmentMode: 'made_to_order',
    leadTimeDaysMin: '',
    leadTimeDaysMax: '',
    requiresDesignApproval: false,
    weight: { value: '', unit: '' },
    hsCode: '',
    countryOfOrigin: '',
    metadata: [],
    productTypeId: '',
    customizationIds: [],
    attributeValues: {},
    options: [],
  };
}

/** The create page's form: `basePrice` being there is what makes the first save create variants. */
export function newProductForm(): ProductFormInput {
  return { ...emptyProductForm(), basePrice: null };
}

function slotFromRow(row: Product['attributeValues'][number]): AttributeFormValue {
  const json = row.valueJson ?? {};
  const html = typeof json.html === 'string' ? json.html : '';
  const dimension = typeof json.value === 'number' ? String(json.value) : '';
  return {
    ids: row.attributeValueIds ?? [],
    text: row.valueText ?? '',
    html,
    number: row.valueNumber === null ? dimension : String(row.valueNumber),
    unit: defaultDimensionUnit(typeof json.unit === 'string' ? json.unit : null),
    bool: row.valueBoolean ?? false,
  };
}

export function fromProduct(product: Product): ProductFormInput {
  const attributeValues: Record<string, AttributeFormValue> = {};
  for (const row of product.attributeValues) {
    attributeValues[row.attributeId] = slotFromRow(row);
  }
  return {
    title: product.title,
    handle: product.handle,
    subtitle: product.subtitle ?? '',
    vendor: product.vendor ?? '',
    skuRoot: product.skuRoot ?? '',
    descriptionHtml: product.descriptionHtml ?? '',
    summary: product.summary ?? '',
    faq: product.faq.map((item) => ({ question: item.question, answer: item.answer })),
    seo: fromSeoOverride(product.seo ?? undefined),
    tags: product.tags,
    searchKeywords: product.searchKeywords,
    status: product.status,
    fulfillmentMode: product.fulfillmentMode,
    leadTimeDaysMin: product.leadTimeDaysMin === null ? '' : String(product.leadTimeDaysMin),
    leadTimeDaysMax: product.leadTimeDaysMax === null ? '' : String(product.leadTimeDaysMax),
    requiresDesignApproval: product.requiresDesignApproval,
    weight: fromWeight(product.weight),
    hsCode: product.hsCode ?? '',
    countryOfOrigin: product.countryOfOrigin ?? '',
    metadata: fromMetadata(product.metadata),
    productTypeId: product.productTypeId ?? '',
    customizationIds: product.customizationIds,
    attributeValues,
    options: product.options.map(fromProductOption),
  };
}

export function fromProductOption(axis: Product['options'][number]): OptionAxisForm {
  return {
    optionId: axis.id,
    attributeId: axis.attributeId,
    name: axis.name,
    values: axis.values.map((v) =>
      v.attributeValueId
        ? {
            valueId: v.id,
            attributeValueId: v.attributeValueId,
            label: v.label,
            swatch: v.swatch === null ? null : { ...v.swatch },
          }
        : {
            valueId: v.id,
            value: v.value,
            label: v.label,
            swatch: v.swatch === null ? null : { ...v.swatch },
          },
    ),
  };
}

/** Number of variants a full cartesian product of the axes would produce. */
export function combinationCount(options: ReadonlyArray<{ values: readonly unknown[] }>): number {
  if (options.length === 0) return 0;
  return options.reduce((n, axis) => n * axis.values.length, 1);
}

/** The bulk `schedule` action's own fields; the page adds `ids` and `action`. */
export type ScheduleRequest = Required<Pick<BulkProductsRequest, 'storeIds'>> &
  Pick<BulkProductsRequest, 'publishAt' | 'unpublishAt'>;

/**
 * "Schedule publishing…" values as the bulk request wants them: wall-clock times read on
 * `timeZone` as instants, an empty time left out (the backend then keeps the stored one).
 */
export function toScheduleRequest(values: BulkScheduleForm, timeZone: string): ScheduleRequest {
  const publishAt = values.publishAt ? fromZonedInput(values.publishAt, timeZone) : null;
  const unpublishAt = values.unpublishAt ? fromZonedInput(values.unpublishAt, timeZone) : null;
  return {
    storeIds: values.storeIds,
    ...(publishAt ? { publishAt } : {}),
    ...(unpublishAt ? { unpublishAt } : {}),
  };
}
